import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { fetchAdminUser, fetchUserRoles, updateUser } from '../../hooks/useAdminUsers';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import UserForm from '../../components/admin/UserForm';

export default function UserEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user: currentUser, isInternal: viewerIsInternal } = useAuth();

  const [userDoc, setUserDoc] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchAdminUser(id), fetchUserRoles()])
      .then(([u, r]) => {
        if (!alive) return;
        setUserDoc(u);
        setRoles(Array.isArray(r) ? r : []);
      })
      .catch((err) => {
        if (alive) setLoadError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const done = () => {
    addToast('User saved', { variant: 'success' });
    navigate('/admin/users');
  };

  if (loading) {
    return <div className="ss-state"><Spinner label="Loading user" size={32} /></div>;
  }

  if (loadError || !userDoc) {
    return <div className="ss-state"><h2>User not found</h2></div>;
  }

  const isSelf = String(userDoc._id) === String(currentUser?.id);
  const targetIsInternal = userDoc.REF_UserType?.Text === 'Internal';
  const locked = !isSelf && targetIsInternal && !viewerIsInternal;

  return (
    <div>
      <p style={{ marginBottom: 'var(--ss-space-3)' }}>
        <Link to="/admin/users" className="ss-back-link"><ArrowLeft size={16} aria-hidden="true" /> Back to Users</Link>
      </p>
      <h1>Edit user</h1>

      <div style={{ marginTop: 'var(--ss-space-5)', maxWidth: '32rem' }}>
        <Card>
          <UserForm
            initial={userDoc}
            roles={roles}
            isSelf={isSelf}
            locked={locked}
            onSubmit={(body) => updateUser(id, body)}
            onDone={done}
            onCancel={() => navigate('/admin/users')}
          />
        </Card>
      </div>
    </div>
  );
}
